import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  METAFIELD_ENTITIES,
  metafieldSchema,
  type Metafield,
  type MetafieldEntity,
} from '@forecastify/shared';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, usePermissionSet } from '../permissions-hook';
import { useWorkspaceNavigate } from '../../workspace-path';
import { useFirstLoad } from '../use-first-load';
import { useSaveToasts } from '../use-save-toasts';
import { MetafieldsScreen, type MetafieldDraft } from './metafields-screen';

/**
 * The Metafieldek admin, with its data.
 *
 * ⚠️ **Every write reloads the whole list, and that is the right trade here.** A metafield list is
 * ten rows, not ten thousand; an optimistic local update would need to replicate the server's key
 * derivation, its `_masolat` walk and its position renumbering — three rules that already exist in
 * one place — to save one request on a screen somebody visits monthly.
 */
export function MetafieldsRoute() {
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the dialog's success toast. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();
  /* ⚠️ R-38 · `go` takes a WORKSPACE-RELATIVE path and supplies the first segment. */
  const go = useWorkspaceNavigate();
  const params = useParams();

  const [fields, setFields] = useState<readonly Metafield[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request('/metafields').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = parseList(metafieldSchema, await response.json());
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setFields(parsed);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /** 26 Oct 2026 · resolves to whether it landed — the dialog closes on `true` only. */
  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { message?: string }[] };
      } | null;
      const code = envelope?.error?.code ?? 'unavailable';
      const detail = envelope?.error?.details?.[0]?.message ?? '';

      /* Branching on `code`, never on `message` — rule 5. */
      if (code === 'duplicate_key')
        setServerError(t('metafields.error.duplicate', { key: detail }));
      else if (code === 'unusable_name' || code === 'validation_failed') {
        setServerError(t('metafields.error.name'));
      } else if (code === 'conflict') setServerError(t('error.conflict'));
      else if (code === 'not_found') setServerError(t('error.not_found'));
      else setServerError(t('error.unavailable'));
      return false;
    }
    await reload();
    return true;
  };

  /** The draft, as the API wants it. Empty optional strings become `null`. */
  const bodyOf = (draft: MetafieldDraft) => ({
    name: draft.name.trim(),
    key: draft.key,
    type: draft.type,
    required: draft.required,
    inList: draft.inList,
    help: draft.help.trim() === '' ? null : draft.help.trim(),
    /* ⚠️ Sent only for a choice type, matching the column's check — an empty array on a text field
     * is what the schema demands, and options on one is what it refuses. */
    options: draft.type === 'select' || draft.type === 'multi' ? draft.options : [],
  });

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <MetafieldsScreen
        loading={loading}
        fields={fields}
        /**
         * ⚠️⚠️ **VALIDATED, NOT CAST — round 5 item 3.** The entity arrives from the address,
         * so `/settings/metafields/banana` is a thing somebody can type. An unknown one is
         * `undefined`, which renders the TYPE LIST rather than an empty page pretending `banana`
         * has no fields — the same treatment `permissions-route.tsx` gives an unknown role id, and
         * what lets `routes.test.tsx` tell the two markers apart.
         */
        entity={METAFIELD_ENTITIES.find((candidate) => candidate === params.entity)}
        onSelect={(entity) => {
          go(`/settings/metafields/${entity}`);
        }}
        canManage={permissions.has('settings.manage')}
        serverError={serverError}
        onCreate={async (draft) => {
          const landed = await write('/metafields', {
            method: 'POST',
            body: JSON.stringify({ entity: draft.entity, ...bodyOf(draft) }),
          });
          if (landed) saved(t('common.toast.created', { name: draft.name.trim() }));
          return landed;
        }}
        onUpdate={async (field, draft) => {
          const landed = await write(`/metafields/${field.id}`, {
            method: 'PUT',
            body: JSON.stringify({ ...bodyOf(draft), version: field.version }),
          });
          if (landed) saved(t('common.toast.saved', { name: draft.name.trim() }));
          return landed;
        }}
        onDuplicate={(field) => {
          void write(`/metafields/${field.id}/duplicate`, { method: 'POST' });
        }}
        onReorder={(entity: MetafieldEntity, ids) => {
          void write(`/metafields/order/${entity}`, {
            method: 'PATCH',
            body: JSON.stringify({ ids }),
          });
        }}
        onDelete={(field) => {
          void write(`/metafields/${field.id}`, { method: 'DELETE' });
        }}
      />
      {toaster}
    </ScreenState>
  );
}
